import React, { useState } from 'react';
import styled from 'styled-components';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const ModalContent = styled.div`
  background-color: #0E1F1F;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  border: 2px solid #284640;
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  @media (max-width: 768px) {
    width: 95%;
    padding: 16px;
    border-radius: 12px;
    gap: 16px;
    margin: 16px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: #FFFFFF;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #FFFFFF;
`;

const Input = styled.input`
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #284640;
  background-color: #172A2A;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #24BA7D;
  }
  
  &::placeholder {
    color: #838D90;
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px; /* Предотвращает zoom на iOS */
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 8px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    padding: 14px 24px;
    font-size: 16px;
    width: 100%;
  }
  
  ${props => props.variant === 'primary' ? `
    background-color: #24BA7D;
    color: #FFFFFF;
    
    &:hover {
      background-color: #1E9A66;
    }
    
    &:disabled {
      background-color: #284640;
      color: #838D90;
      cursor: not-allowed;
    }
  ` : `
    background-color: transparent;
    color: #838D90;
    border: 1px solid #284640;
    
    &:hover {
      background-color: #172A2A;
      color: #FFFFFF;
    }
  `}
`;

interface CreateShipmentStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSubmit: (status: string) => Promise<void>;
}

const CreateShipmentStatusModal: React.FC<CreateShipmentStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onSubmit
}) => {
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!status.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(status.trim());
      handleClose();
      onSuccess();
    } catch (error) {
      console.error('Ошибка при создании статуса:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStatus('');
    setLoading(false);
    onClose();
  };

  const isFormValid = status.trim().length > 0;

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Создать новый статус</ModalTitle>
        </ModalHeader>

        <FormField>
          <Label>Название статуса</Label>
          <Input
            type="text"
            placeholder="Введите название статуса"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={loading}
            maxLength={100}
          />
        </FormField>

        <ButtonContainer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? 'Создание...' : 'Создать статус'}
          </Button>
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateShipmentStatusModal;
